import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import {
  MARITAL_STATUS_OPTIONS,
  getRegisterRedirectPath,
  isMarriedUser,
  syncMaritalStatus,
} from "../constants/maritalStatus";
import useSeo from "../hooks/useSeo";
import { SEO_PAGES } from "../constants/seo";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const RESEND_SECONDS = 60;

export default function Register() {
  const navigate = useNavigate();
  useSeo(SEO_PAGES.register);

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const validate = () => {
    const newErrors = {};

    if (!phone) {
      newErrors.phone = "手机号不能为空";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "手机号格式不正确";
    }

    if (!email) {
      newErrors.email = "邮箱不能为空";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "邮箱格式不正确";
    }

    if (!emailCode.trim()) {
      newErrors.emailCode = "请填写邮箱验证码";
    }

    if (!password) {
      newErrors.password = "密码不能为空";
    } else if (password.length < 6) {
      newErrors.password = "密码至少6位";
    }

    if (!maritalStatus) {
      newErrors.maritalStatus = "请选择婚姻状况";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmailCode = async () => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: "请先填写正确的手机号" }));
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrors((prev) => ({ ...prev, email: "请先填写正确的邮箱" }));
      return;
    }
    if (countdown > 0) return;

    setSendingCode(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role: "user",
          purpose: "register",
          email: email.trim(),
          phone: phone.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "验证码发送失败");
        return;
      }
      setEmailMasked(data.email_masked || "");
      setCountdown(RESEND_SECONDS);
      alert(
        data.dev_hint
          ? `${data.message}\n（开发模式：请查看后端控制台验证码）`
          : `验证码已发送到 ${data.email_masked || email.trim()}，请查收邮件（含垃圾箱）`
      );
    } catch (err) {
      console.error(err);
      alert("请求失败，请检查后端是否启动");
    } finally {
      setSendingCode(false);
    }
  };

  const handleRegister = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: phone.trim(),
          email: email.trim(),
          email_code: emailCode.trim(),
          password,
          name: "新用户",
          city: "未知",
          marital_status: maritalStatus,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || data.message || "注册失败");
        return;
      }

      const savedMaritalStatus = data.marital_status || maritalStatus;

      localStorage.setItem("user_id", data.user_id);
      syncMaritalStatus(savedMaritalStatus);

      alert(
        isMarriedUser(savedMaritalStatus)
          ? "注册成功，即将进入婚姻中心"
          : "注册成功，请先完善交友资料"
      );

      navigate(getRegisterRedirectPath(savedMaritalStatus));
    } catch (err) {
      console.error("❌ fetch异常:", err);
      alert("请求失败，请检查后端是否启动");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    {
      title: "单身用户",
      desc: "寻找对象 · AI匹配",
      color: "from-pink-500 to-rose-500",
      value: "未婚",
    },
    {
      title: "已婚用户",
      desc: "婚姻修复 · 关系提升",
      color: "from-rose-400 to-pink-500",
      value: "已婚",
    },
    {
      title: "咨询师入驻",
      desc: "接单变现 · 平台分发",
      color: "from-purple-500 to-indigo-500",
      link: "/registerConsultants",
    },
    {
      title: "机构入驻",
      desc: "品牌曝光 · 获客增长",
      color: "from-indigo-500 to-purple-500",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-pink-50 to-purple-50">
      <Header />

      <div className="flex-1 flex justify-center px-4 mt-[10px] mb-[10px]">
        <div className="max-w-5xl w-full">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-800">
            AI婚恋平台注册
          </h2>

          <div className="grid md:grid-cols-4 gap-6 mb-10">
            {roles.map((item, i) => (
              <div
                key={i}
                onClick={() => {
                  if (item.value) {
                    setMaritalStatus(item.value);
                    setErrors((prev) => ({ ...prev, maritalStatus: "" }));
                  } else if (item.link) {
                    navigate(item.link);
                  }
                }}
                className={`p-6 rounded-2xl text-white bg-gradient-to-r ${item.color} shadow-lg cursor-pointer transition hover:scale-[1.02] ${
                  item.value && maritalStatus === item.value
                    ? "ring-4 ring-white/80"
                    : ""
                }`}
              >
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm opacity-90">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white p-8 rounded-2xl shadow max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-2 text-center">手机号注册</h3>
            <p className="text-sm text-gray-400 text-center mb-6">
              请填写手机号与常用邮箱，验证码将发送到邮箱（请查收垃圾箱）
            </p>

            <div className="space-y-5">
              <div>
                <input
                  type="text"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setErrors({ ...errors, phone: "" });
                  }}
                  className={`w-full border p-3 rounded-lg ${
                    errors.phone ? "border-red-500" : ""
                  }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <input
                  type="email"
                  placeholder="请输入常用邮箱"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors({ ...errors, email: "" });
                  }}
                  className={`w-full border p-3 rounded-lg ${
                    errors.email ? "border-red-500" : ""
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="邮箱验证码"
                    value={emailCode}
                    onChange={(e) => {
                      setEmailCode(e.target.value);
                      setErrors({ ...errors, emailCode: "" });
                    }}
                    className={`flex-1 border p-3 rounded-lg ${
                      errors.emailCode ? "border-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={sendEmailCode}
                    disabled={sendingCode || countdown > 0}
                    className="shrink-0 min-w-[120px] rounded-lg bg-pink-500 px-3 py-3 text-sm text-white disabled:opacity-50"
                  >
                    {sendingCode
                      ? "发送中…"
                      : countdown > 0
                      ? `${countdown}s`
                      : "获取验证码"}
                  </button>
                </div>
                {emailMasked ? (
                  <p className="text-xs text-blue-600 mt-2">
                    验证码已发送至：{emailMasked}
                  </p>
                ) : null}
                {errors.emailCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.emailCode}</p>
                )}
              </div>

              <div>
                <input
                  type="password"
                  placeholder="请输入密码（至少6位）"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: "" });
                  }}
                  className={`w-full border p-3 rounded-lg ${
                    errors.password ? "border-red-500" : ""
                  }`}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <select
                  value={maritalStatus}
                  onChange={(e) => {
                    setMaritalStatus(e.target.value);
                    setErrors({ ...errors, maritalStatus: "" });
                  }}
                  className={`w-full border p-3 rounded-lg bg-white ${
                    errors.maritalStatus ? "border-red-500" : ""
                  }`}
                >
                  <option value="">请选择婚姻状况</option>
                  {MARITAL_STATUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {errors.maritalStatus && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.maritalStatus}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {isMarriedUser(maritalStatus)
                    ? "已婚用户将进入婚姻中心，侧重咨询与关系修复"
                    : maritalStatus
                    ? "将进入个人中心，侧重交友匹配与资料完善"
                    : "也可点击上方卡片快速选择"}
                </p>
              </div>

              <button
                onClick={handleRegister}
                disabled={loading}
                className={`w-full py-3 rounded-lg text-white ${
                  loading ? "bg-gray-300" : "bg-pink-500 hover:bg-pink-600"
                }`}
              >
                {loading ? "注册中..." : "注册"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
